import * as ProgressPrimitive from "@radix-ui/react-progress";
import { forwardRef } from "react";
import { system, margin } from "styled-system";
import styled, { keyframes } from "styled-components";
import { CheckCircleFilled, CloseCircleFilled, CheckOutlined, CloseOutlined } from "../../icons";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VIEW_BOX_SIZE = 100;
const STROKE_WIDTH = 6;
const filledIconMap = {
  default: null,
  active: null,
  success: CheckCircleFilled,
  error: CloseCircleFilled
};
const outlinedIconMap = {
  default: null,
  active: null,
  success: CheckOutlined,
  error: CloseOutlined
};
const color = system({
  bg: {
    property: "background",
    scale: "colors"
  },
  stroke: {
    property: "stroke",
    scale: "colors"
  }
});
const activeProgress = keyframes(["0%{transform:translateX(-100%) scaleX(0);opacity:0.1;}20%{transform:translateX(-100%) scaleX(0);opacity:0.7;}100%{transform:translateX(0) scaleX(1);opacity:0;}"]);
const ProgressLineIndicator = styled(ProgressPrimitive.Indicator).withConfig({
  displayName: "progress__ProgressLineIndicator",
  componentId: "sc-1plz9y0-0"
})(["width:100%;height:100%;border-radius:inherit;animation-timing-function:", ";transition:transform 0.3s;", ""], p => p.theme.transitions.easeOutCircle, color);
const ProgressCircleIndicator = styled.path.withConfig({
  displayName: "progress__ProgressCircleIndicator",
  componentId: "sc-1plz9y0-1"
})(["animation-timing-function:", ";transition:stroke-dashoffset 0.3s,stroke-dasharray 0.3s,stroke 0.3s;", ""], p => p.theme.transitions.easeOutCircle, color);
const ProgressCircleTrack = styled.path.withConfig({
  displayName: "progress__ProgressCircleTrack",
  componentId: "sc-1plz9y0-2"
})(["", ""], color);
const ProgressLineText = styled.span.withConfig({
  displayName: "progress__ProgressLineText",
  componentId: "sc-1plz9y0-3"
})(["display:inline-block;min-width:50px;margin-left:8px;font-size:1em;line-height:1;white-space:nowrap;text-align:left;vertical-align:middle;word-break:normal;flex:none;color:", ";.anticon{font-size:1.1em;}"], p => p.theme.colors.text);
const ProgressCircleText = styled.span.withConfig({
  displayName: "progress__ProgressCircleText",
  componentId: "sc-1plz9y0-4"
})(["position:absolute;top:50%;left:50%;width:100%;margin:0;padding:0;line-height:1;white-space:normal;text-align:center;transform:translate(-50%,-50%);color:", ";.anticon{font-size:1.1em;}"], p => p.theme.colors.text);
const ProgressLineRoot = styled(ProgressPrimitive.Root).withConfig({
  displayName: "progress__ProgressLineRoot",
  componentId: "sc-1plz9y0-5"
})(["position:relative;display:inline-block;vertical-align:middle;overflow:hidden;width:100%;height:8px;border-radius:100px;transform:translateZ(0);", ""], color);
const ProgressCircleRoot = styled(ProgressPrimitive.Root).withConfig({
  displayName: "progress__ProgressCircleRoot",
  componentId: "sc-1plz9y0-6"
})(["position:relative;width:120px;height:120px;font-size:", "px;"], p => p.theme.fontSizes[5]);
const ProgressContainer = styled.div.withConfig({
  displayName: "progress__ProgressContainer",
  componentId: "sc-1plz9y0-7"
})(["display:inline-flex;align-items:center;width:100%;font-size:", "px;", " &[data-size=\"small\"]{font-size:", "px;", "{min-width:45px;}", "{height:6px;}", "{width:80px;height:80px;font-size:", "px;}}&[data-status=\"active\"]{", "::before{position:absolute;top:0;right:0;bottom:0;left:0;background:#fff;border-radius:inherit;opacity:0;animation:", " 2s ", " infinite;content:\"\";}}&[data-status=\"success\"]{", "{background:", ";}", "{stroke:", ";}.anticon{color:", ";}}&[data-status=\"error\"]{", "{background:", ";}", "{stroke:", ";}.anticon{color:", ";}}"], p => p.theme.fontSizes.md, margin, p => p.theme.fontSizes.sm, ProgressLineText, ProgressLineRoot, ProgressCircleRoot, p => p.theme.fontSizes[3], ProgressLineIndicator, activeProgress, p => p.theme.transitions.easeOutQuint, ProgressLineIndicator, p => p.theme.colors.positive.base, ProgressCircleIndicator, p => p.theme.colors.positive.base, p => p.theme.colors.positive.base, ProgressLineIndicator, p => p.theme.colors.negative.base, ProgressCircleIndicator, p => p.theme.colors.negative.base, p => p.theme.colors.negative.base);
export const ProgressCircle = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    value = 0,
    max = 100,
    showInfo = true,
    size = "default",
    status = "default",
    indicatorColor = "info.base",
    trackColor = "bg.base",
    getValueLabel,
    ...rest
  } = _ref;
  const percent = Math.round(value / max * 100);
  const radius = VIEW_BOX_SIZE / 2 - STROKE_WIDTH / 2;
  const perimeter = Math.PI * 2 * radius;
  const strokeOffset = (100 - percent) / 100 * perimeter;
  const Icon = outlinedIconMap[status] ?? null;
  return /*#__PURE__*/_jsx(ProgressContainer, {
    ref: ref,
    ...rest,
    "data-size": size,
    "data-status": status,
    children: /*#__PURE__*/_jsxs(ProgressCircleRoot, {
      value: value,
      max: max,
      getValueLabel: getValueLabel,
      children: [/*#__PURE__*/_jsxs("svg", {
        viewBox: `0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`,
        children: [/*#__PURE__*/_jsx(ProgressCircleTrack, {
          d: "M 50,50 m 0,-47 a 47,47 0 1 1 0,94 a 47,47 0 1 1 0,-94",
          stroke: trackColor,
          strokeWidth: STROKE_WIDTH,
          strokeLinecap: "round",
          fillOpacity: "0"
        }), /*#__PURE__*/_jsx(ProgressCircleIndicator, {
          d: "M 50,50 m 0,-47 a 47,47 0 1 1 0,94 a 47,47 0 1 1 0,-94",
          stroke: indicatorColor,
          strokeWidth: STROKE_WIDTH,
          strokeLinecap: "round",
          fillOpacity: "0",
          style: {
            strokeDasharray: `${perimeter}px ${perimeter}px`,
            strokeDashoffset: `${strokeOffset}px`,
            opacity: percent > 0 ? 1 : 0
          }
        })]
      }), showInfo ? /*#__PURE__*/_jsx(ProgressCircleText, {
        "aria-hidden": true,
        children: Icon ? /*#__PURE__*/_jsx(Icon, {}) : `${value}%`
      }) : null]
    })
  });
});
export const Progress = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    value = 0,
    max = 100,
    showInfo = true,
    size = "default",
    status = "default",
    indicatorColor = "info.base",
    trackColor = "bg.base",
    getValueLabel,
    ...rest
  } = _ref2;
  const percent = Math.round(value / max * 100);
  const Icon = filledIconMap[status] ?? null;
  return /*#__PURE__*/_jsxs(ProgressContainer, {
    ref: ref,
    ...rest,
    "data-size": size,
    "data-status": status,
    children: [/*#__PURE__*/_jsx(ProgressLineRoot, {
      value: value,
      max: max,
      bg: trackColor,
      getValueLabel: getValueLabel,
      children: /*#__PURE__*/_jsx(ProgressLineIndicator, {
        bg: indicatorColor,
        style: {
          transform: `translateX(-${100 - percent}%)`
        }
      })
    }), showInfo ? /*#__PURE__*/_jsx(ProgressLineText, {
      "aria-hidden": true,
      children: Icon ? /*#__PURE__*/_jsx(Icon, {}) : `${value}%`
    }) : null]
  });
});
export default Object.assign(Progress, {
  Circle: ProgressCircle
});